/**
 * Language tag and language names.
 *
 * The short tag (EN, EL) is how a policy's language is shown next to its name.
 * The full name rides along as a tooltip and as the accessible label, because
 * "EL" alone is not guessable by someone who does not already know it is the
 * ISO code for Greek.
 *
 * NAMES COME FROM THE BROWSER, NOT FROM THE TRANSLATION FILES
 * `Intl.DisplayNames` knows all ten supported languages in every interface
 * language, so this does not depend on the i18n bundles having a `language.xx`
 * entry for each (they hold two today). If a runtime lacks it, or is given a
 * code it does not know, the code itself is shown — never an empty label.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/cn';

/** Ship these as English defaults; keys are the i18n keys. */
export const LANGUAGE_TAG_STRINGS = {
  'policies.languageTagLabel': 'Language: {{name}}',
} as const;

/** Name of a language code in the given interface language, or the code if unknown. */
export function languageName(code: string, uiLanguage: string): string {
  try {
    const name = new Intl.DisplayNames([uiLanguage], { type: 'language' }).of(code);
    if (name && name.toLowerCase() !== code.toLowerCase()) {
      return name.charAt(0).toLocaleUpperCase(uiLanguage) + name.slice(1);
    }
  } catch {
    // Unsupported locale or malformed code: fall through to the code.
  }
  return code.toUpperCase();
}

export function useLanguageName() {
  const { i18n } = useTranslation();
  return useMemo(() => (code: string) => languageName(code, i18n.language), [i18n.language]);
}

export function LanguageTag({ code, className }: { code: string; className?: string }) {
  const { t } = useTranslation();
  const name = useLanguageName()(code);
  const label = t('policies.languageTagLabel', {
    defaultValue: LANGUAGE_TAG_STRINGS['policies.languageTagLabel'],
    name,
  });
  return (
    <span
      title={name}
      aria-label={label}
      className={cn(
        'inline-flex items-center rounded border border-line bg-surface-sunken px-1.5 py-px',
        'font-mono text-[11px] font-semibold leading-4 tracking-wide text-ink-secondary uppercase',
        className,
      )}
    >
      {code}
    </span>
  );
}
